import {Component, computed, DestroyRef, effect, inject, input, output, viewChild} from '@angular/core';
import {FullCalendarComponent, FullCalendarModule} from '@fullcalendar/angular';
import {CalendarOptions, EventClickArg, DateSelectArg, DatesSetArg, EventContentArg, EventDropArg, EventInput} from '@fullcalendar/core';
import {EventResizeDoneArg} from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, {DateClickArg} from '@fullcalendar/interaction';
import {Appointment, CalendarAppointment} from '@xmedical/core';
import {MatDialog} from '@angular/material/dialog';
import dayjs from 'dayjs/esm';

const HELPER_EVENT_ID = 'helper';
const SLOT_DURATION_MS = 15 * 60 * 1000;

export interface AppointmentMoveRequest {
  appointment: CalendarAppointment;
  start: Date;
  end?: Date;
  revert: () => void;
}

@Component({
  selector: 'app-fullcalendar-view',
  templateUrl: './fullcalendar-view.component.html',
  styleUrls: ['./fullcalendar-view.component.scss'],
  imports: [FullCalendarModule]
})
export class FullcalendarViewComponent {
  // Inputs
  currentDate = input.required<Date>();
  view = input.required<string>();
  visibleAppointmentTypes = input<number[]>([]);
  practiceLocationId = input<number | null>(null);
  practicePractitionerId = input<number | null>(null);
  events = input<EventInput[]>([]);
  defaultDurationMinutes = input<number>(30);
  allowDragCreate = input<boolean>(true);
  allowTapCreate = input<boolean>(true);
  allowDragEditing = input<boolean>(false);

  // Outputs
  appointmentClicked = output<CalendarAppointment>();
  helperEventClicked = output<{ start: Date; end: Date }>();
  dateRangeChanged = output<{ startDate: string; endDate: string }>();
  appointmentMoved = output<AppointmentMoveRequest>();

  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  private calendarComponent = viewChild(FullCalendarComponent);

  // Filtered events based on visible appointment types
  readonly filteredEvents = computed<EventInput[]>(() => {
    const events = this.events();
    const visible = this.visibleAppointmentTypes();
    if (!visible || visible.length === 0) {
      return events;
    }
    return events.filter(event => {
      const appointment = event.extendedProps?.appointment as CalendarAppointment;
      return appointment && visible.includes(appointment.appointmentType?.id);
    });
  });

  private fullCalendarView = computed(() => {
    const v = this.view();
    if (v === 'day') return 'timeGridDay';
    if (v === 'week') return 'timeGridWeek';
    if (v === 'month') return 'dayGridMonth';
    return 'timeGridWeek';
  });

  readonly calendarOptions = computed<CalendarOptions>(() => ({
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    initialView: this.fullCalendarView(),
    initialDate: this.currentDate(),
    firstDay: 1,
    headerToolbar: false,
    slotMinTime: '07:00:00',
    slotMaxTime: '18:00:00',
    slotDuration: '00:15:00',
    slotEventOverlap: false,
    allDaySlot: false,
    editable: this.allowDragEditing(),
    eventStartEditable: this.allowDragEditing(),
    eventDurationEditable: this.allowDragEditing(),
    selectable: this.allowDragCreate(),
    selectMirror: true,
    events: this.filteredEvents(),
    eventClick: (info: EventClickArg) => this.onEventClick(info),
    select: (info: DateSelectArg) => this.onSelect(info),
    dateClick: (info: DateClickArg) => this.onDateClick(info),
    datesSet: (info: DatesSetArg) => this.onDatesSet(info),
    eventContent: (arg: EventContentArg) => this.renderEventContent(arg),
    eventDrop: (info: EventDropArg) => this.onEventDrop(info),
    eventResize: (info: EventResizeDoneArg) => this.onEventResize(info),
    height: 'auto',
    nowIndicator: true,
    weekends: true,
    dayMaxEvents: true,
    eventDisplay: 'block',
  }));

  constructor() {
    this.destroyRef.onDestroy(() => this.removeHelperEvent());
  }

  // Navigate FullCalendar when inputs change
  private dateEffect = effect(() => {
    const date = this.currentDate();
    const cal = this.calendarComponent();
    if (cal) {
      const api = cal.getApi();
      if (api) {
        api.gotoDate(date);
      }
    }
  });

  private viewEffect = effect(() => {
    const fcView = this.fullCalendarView();
    const cal = this.calendarComponent();
    if (cal) {
      const api = cal.getApi();
      if (api) {
        api.changeView(fcView);
      }
    }
  });

  removeHelperEvent(): void {
    const api = this.calendarComponent()?.getApi();
    if (api) {
      api.getEventById(HELPER_EVENT_ID)?.remove();
    }
  }

  private onEventClick(info: EventClickArg): void {
    if (this.dialog.openDialogs.length > 0) return;

    if (info.event.id === HELPER_EVENT_ID) {
      const start = info.event.start!;
      const end = info.event.end!;
      this.helperEventClicked.emit({start, end});
      return;
    }

    const appointment = info.event.extendedProps['appointment'] as CalendarAppointment;
    if (appointment) {
      this.appointmentClicked.emit(appointment);
    }
  }

  private renderEventContent(arg: EventContentArg): {domNodes: Node[]} {
    const container = document.createElement('div');
    container.className = 'fc-event-content-inner';

    if (arg.timeText) {
      const time = document.createElement('div');
      time.className = 'fc-event-time';
      time.textContent = arg.timeText;
      container.appendChild(time);
    }

    const appointment = arg.event.extendedProps['appointment'] as CalendarAppointment | undefined;
    const statusColor = appointment?.patient?.status?.color;

    const title = document.createElement('div');
    title.className = 'fc-event-title';
    if (statusColor) {
      const dot = document.createElement('span');
      dot.className = 'fc-event-status-dot';
      dot.style.backgroundColor = statusColor;
      title.appendChild(dot);
    }
    const titleText = document.createTextNode(arg.event.title);
    title.appendChild(titleText);
    container.appendChild(title);

    const flags = appointment?.patient?.flags ?? [];
    if (flags.length > 0) {
      const pillsWrap = document.createElement('div');
      pillsWrap.className = 'fc-event-flags';
      flags.forEach(flag => {
        const pill = document.createElement('span');
        pill.className = 'fc-event-flag-pill';
        if (flag.color) pill.style.backgroundColor = flag.color;
        pill.textContent = flag.name ?? '';
        pillsWrap.appendChild(pill);
      });
      container.appendChild(pillsWrap);
    }

    return {domNodes: [container]};
  }

  private onSelect(info: DateSelectArg): void {
    if (this.dialog.openDialogs.length > 0) {
      this.calendarComponent()?.getApi()?.unselect();
      return;
    }

    const api = this.calendarComponent()?.getApi();
    if (!api) return;

    // Remove any existing helper event
    this.removeHelperEvent();

    const start = info.start;
    const selectionMs = info.end.getTime() - start.getTime();
    const isClick = info.allDay || selectionMs <= SLOT_DURATION_MS;
    const end = isClick
      ? new Date(start.getTime() + this.defaultDurationMinutes() * 60 * 1000)
      : info.end;

    api.addEvent({
      id: HELPER_EVENT_ID,
      title: 'Select to Create Appointment.',
      start,
      end,
      classNames: ['fc-helper-event'],
      extendedProps: {isHelper: true},
    });

    api.unselect();
  }

  private onDateClick(info: DateClickArg): void {
    if (!this.allowTapCreate()) return;
    if (this.dialog.openDialogs.length > 0) return;
    if (!info.view.type.startsWith('timeGrid')) return;

    this.removeHelperEvent();

    const start = info.date;
    const end = new Date(start.getTime() + this.defaultDurationMinutes() * 60 * 1000);
    this.helperEventClicked.emit({start, end});
  }

  private onEventDrop(info: EventDropArg): void {
    this.emitMoveRequest(info.event.extendedProps['appointment'], info.event.start, info.event.end, info.revert);
  }

  private onEventResize(info: EventResizeDoneArg): void {
    this.emitMoveRequest(info.event.extendedProps['appointment'], info.event.start, info.event.end, info.revert);
  }

  private emitMoveRequest(
    appointmentProp: unknown,
    start: Date | null,
    end: Date | null,
    revert: () => void,
  ): void {
    const appointment = appointmentProp as CalendarAppointment | undefined;
    if (!appointment || !start) {
      revert();
      return;
    }
    this.appointmentMoved.emit({
      appointment,
      start,
      end: end ?? undefined,
      revert,
    });
  }

  private onDatesSet(info: DatesSetArg): void {
    const startDate = dayjs(info.start).format('YYYY-MM-DD');
    const endDate = dayjs(info.end).format('YYYY-MM-DD');
    this.dateRangeChanged.emit({startDate, endDate});
  }
}
